import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PreviewPane from './PreviewPane';
import { documentsCreate } from '../utils/templatesApi';
import { exportToPdf } from '../utils/exportPdf';

const STEPS = [
    { key: 'context', label: 'Context' },
    { key: 'variables', label: 'Fill Variables' },
    { key: 'preview', label: 'Preview' },
    { key: 'export', label: 'Export' },
];

const GenerateWizardModal = ({ firebase, template, onClose }) => {
    const history = useHistory();
    const [step, setStep] = useState(0);
    const [context, setContext] = useState('none'); // none, project, deal
    const [values, setValues] = useState(() => {
        // Initialize with default values
        const defaults = {};
        template.variables?.forEach((v) => {
            if (v.defaultValue) {
                defaults[v.key] = v.defaultValue;
            }
        });
        return defaults;
    });
    const [exporting, setExporting] = useState(false);
    const [exported, setExported] = useState(false);
    const previewRef = useRef(null);

    const currentStep = STEPS[step];

    // Validation
    const requiredVariables = useMemo(
        () => template.variables?.filter((v) => v.required) || [],
        [template.variables]
    );

    const missingRequired = useMemo(
        () => requiredVariables.filter((v) => !values[v.key]?.toString().trim()),
        [requiredVariables, values]
    );

    const canProceed = useMemo(() => {
        if (currentStep.key === 'variables') {
            return missingRequired.length === 0;
        }
        return true;
    }, [currentStep, missingRequired]);

    const handleValueChange = useCallback((key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setStep(step - 1);
        }
    };

    const handleExport = async () => {
        if (!firebase) return;

        setExporting(true);
        try {
            // Save document metadata (best effort; do not block PDF if backend unavailable)
            const documentId = `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            try {
                await documentsCreate(firebase, documentId, {
                    templateId: template.id,
                    templateTitle: template.title,
                    values,
                    status: 'generated',
                });
            } catch (saveErr) {
                console.error('Failed to save document metadata:', saveErr);
            }

            // Generate PDF
            if (previewRef.current) {
                await exportToPdf(previewRef.current, template.title || 'Document');
            }

            setExported(true);
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    const renderVariableInput = (variable) => {
        const value = values[variable.key] || '';

        const commonProps = {
            value,
            onChange: (e) => handleValueChange(variable.key, e.target.value),
            placeholder: variable.label,
        };

        switch (variable.type) {
            case 'date':
                return <input type="date" {...commonProps} />;
            case 'number':
                return <input type="number" {...commonProps} />;
            case 'currency':
                return <input type="text" {...commonProps} placeholder={`₹ ${variable.label}`} />;
            default:
                return <input type="text" {...commonProps} />;
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-container" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Generate Document</h2>
                    <button className="modal-close" onClick={onClose}>
                        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="modal-body">
                    {/* Progress Steps */}
                    <div className="wizard-steps">
                        {STEPS.map((s, i) => (
                            <div
                                key={s.key}
                                className={`wizard-step ${i === step ? 'active' : ''} ${i < step ? 'completed' : ''}`}
                            >
                                <div className="wizard-step-number">Step {i + 1}</div>
                                <div className="wizard-step-label">{s.label}</div>
                            </div>
                        ))}
                    </div>

                    {/* Step Content */}
                    {currentStep.key === 'context' && (
                        <div>
                            <p style={{ color: 'rgba(248,248,248,0.7)', marginBottom: '16px' }}>
                                Select a context for auto-filling variables (optional).
                            </p>

                            <div style={{ display: 'flex', gap: '12px' }}>
                                <button
                                    className={`template-btn ${context === 'none' ? 'primary' : 'secondary'}`}
                                    onClick={() => setContext('none')}
                                    style={{ flex: 1 }}
                                >
                                    No Context
                                </button>
                                <button
                                    className={`template-btn ${context === 'project' ? 'primary' : 'secondary'}`}
                                    onClick={() => setContext('project')}
                                    style={{ flex: 1 }}
                                    disabled
                                    title="Coming soon"
                                >
                                    From Project
                                </button>
                                <button
                                    className={`template-btn ${context === 'deal' ? 'primary' : 'secondary'}`}
                                    onClick={() => setContext('deal')}
                                    style={{ flex: 1 }}
                                    disabled
                                    title="Coming soon"
                                >
                                    From Deal
                                </button>
                            </div>

                            <p style={{ color: 'rgba(248,248,248,0.4)', fontSize: '12px', marginTop: '12px' }}>
                                Auto-fill from Projects/Deals coming soon. For now, fill variables manually.
                            </p>
                        </div>
                    )}

                    {currentStep.key === 'variables' && (
                        <div>
                            <p style={{ color: 'rgba(248,248,248,0.7)', marginBottom: '20px' }}>
                                Fill in the values for each variable. Required fields are marked with *.
                            </p>

                            {template.variables?.length === 0 ? (
                                <p style={{ color: 'rgba(248,248,248,0.5)' }}>
                                    This template has no variables. Proceed to preview.
                                </p>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                                    {template.variables?.map((variable) => (
                                        <div key={variable.key} className="form-group" style={{ margin: 0 }}>
                                            <label>
                                                {variable.label}
                                                {variable.required && <span className="required">*</span>}
                                            </label>
                                            {renderVariableInput(variable)}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {missingRequired.length > 0 && (
                                <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '16px' }}>
                                    Please fill all required fields: {missingRequired.map((v) => v.label).join(', ')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Preview step - visible */}
                    {currentStep.key === 'preview' && (
                        <div ref={previewRef}>
                            <PreviewPane template={template} values={values} />
                        </div>
                    )}

                    {/* Export step - keep preview offscreen for PDF capture */}
                    {currentStep.key === 'export' && (
                        <div
                            ref={previewRef}
                            style={{
                                position: 'absolute',
                                left: '-9999px',
                                top: 0,
                                width: '210mm',
                                background: '#ffffff',
                            }}
                        >
                            <PreviewPane template={template} values={values} />
                        </div>
                    )}

                    {currentStep.key === 'export' && (
                        <div style={{ textAlign: 'center', padding: '20px' }}>
                            {exported ? (
                                <>
                                    <svg
                                        width="64"
                                        height="64"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="#22c55e"
                                        style={{ margin: '0 auto 16px' }}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <h3 style={{ color: '#f8f8f8', marginBottom: '8px' }}>Document Generated!</h3>
                                    <p style={{ color: 'rgba(248,248,248,0.6)', marginBottom: '24px' }}>
                                        Your PDF has been downloaded and saved to your documents.
                                    </p>
                                    <button
                                        className="editor-btn secondary"
                                        onClick={() => history.push('/franchise-dashboard/templates/documents')}
                                    >
                                        View All Documents
                                    </button>
                                </>
                            ) : (
                                <>
                                    <h3 style={{ color: '#f8f8f8', marginBottom: '8px' }}>Ready to Export</h3>
                                    <p style={{ color: 'rgba(248,248,248,0.6)', marginBottom: '24px' }}>
                                        Click below to generate and download your document as PDF.
                                    </p>
                                    <button
                                        className="editor-btn primary"
                                        onClick={handleExport}
                                        disabled={exporting}
                                        style={{ padding: '14px 32px', fontSize: '14px' }}
                                    >
                                        {exporting ? 'Generating PDF...' : 'Download PDF'}
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>

                <div className="modal-footer">
                    {step > 0 && !exported && (
                        <button className="editor-btn secondary" onClick={handleBack}>
                            Back
                        </button>
                    )}
                    <div style={{ flex: 1 }} />
                    {step < STEPS.length - 1 && (
                        <button
                            className="editor-btn primary"
                            onClick={handleNext}
                            disabled={!canProceed}
                        >
                            Next
                        </button>
                    )}
                    {exported && (
                        <button className="editor-btn primary" onClick={onClose}>
                            Done
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GenerateWizardModal;
